import { computed, unref } from "vue";
import {
  SIMILARITY_APPROVAL_REQUIRED_MESSAGE,
} from "../utils/adminQuestionEditorHelpers.mjs";

export { SIMILARITY_APPROVAL_REQUIRED_MESSAGE };

const hasText = (value) => typeof value === "string" && value.trim().length > 0;

const normalizeAnswerImageId = (value) => {
  if (Array.isArray(value)) {
    return typeof value[0] === "string" ? value[0].trim() : "";
  }
  if (typeof value === "string") {
    return value.trim();
  }
  return "";
};

const optionHasImage = (option) =>
  normalizeAnswerImageId(option?.answerImageId ?? option?.answerImageIds ?? "")
    .length > 0;

const hasOptionContent = (option) =>
  hasText(option?.text ?? "") || optionHasImage(option);

const normalizeExplanationMode = (mode) => {
  if (mode === undefined) return null;
  const resolved = unref(mode);
  return resolved === undefined ? null : resolved;
};

const hasExplanationReference = (explanationRef) => {
  if (!explanationRef) return false;

  if (typeof explanationRef === "string") {
    return explanationRef.trim().length > 0;
  }

  if (typeof explanationRef === "object") {
    return hasText(explanationRef.id) || hasText(explanationRef.path);
  }

  return false;
};

const hasCategoryReference = (category) => {
  if (!category) return false;

  if (typeof category === "string") {
    return category.trim().length > 0;
  }

  if (typeof category === "object") {
    return hasText(category.id) || hasText(category.path);
  }

  return false;
};

export const useQuestionApprovalValidation = (
  questionDataRef,
  {
    explanationEditorMode,
    requiresSimilarityApproval = false,
    similarityApprovalReady = true,
  } = {},
) => {
  const questionData = computed(() => unref(questionDataRef) || {});

  const questionTextPresent = computed(() =>
    hasText(questionData.value?.text ?? ""),
  );

  const categorySelected = computed(() =>
    hasCategoryReference(questionData.value?.category),
  );

  const options = computed(() => {
    const rawOptions = questionData.value?.options;
    return Array.isArray(rawOptions) ? rawOptions : [];
  });

  const allOptionsHaveContent = computed(() =>
    options.value.every((option) => hasOptionContent(option)),
  );

  const missingOptionLabels = computed(() => {
    const missing = options.value
      .map((option, index) =>
        hasOptionContent(option) ? null : `Option ${index + 1}`,
      )
      .filter(Boolean);

    return missing.length > 0 ? missing.join(", ") : "";
  });

  const explanationLinked = computed(() =>
    hasExplanationReference(questionData.value?.explanationRef),
  );

  const explanationMode = computed(() =>
    normalizeExplanationMode(explanationEditorMode),
  );

  const isCurrentlyEditingExplanation = computed(() =>
    explanationEditorMode === undefined
      ? false
      : explanationMode.value !== null,
  );

  const requiresSimilarityApprovalCheck = computed(() =>
    Boolean(unref(requiresSimilarityApproval)),
  );

  const similarityApprovalReadyState = computed(
    () =>
      !requiresSimilarityApprovalCheck.value ||
      Boolean(unref(similarityApprovalReady)),
  );

  const isQuestionComplete = computed(
    () =>
      questionTextPresent.value &&
      categorySelected.value &&
      allOptionsHaveContent.value &&
      explanationLinked.value &&
      !isCurrentlyEditingExplanation.value &&
      similarityApprovalReadyState.value,
  );

  const unmetApprovalCriteria = computed(() => {
    const issues = [];

    if (!questionTextPresent.value) {
      issues.push("Question text is required.");
    }

    if (!categorySelected.value) {
      issues.push("Category must be selected.");
    }

    if (!allOptionsHaveContent.value) {
      issues.push(
        `Complete all answer options (text or image). Missing: ${missingOptionLabels.value}`,
      );
    }

    if (isCurrentlyEditingExplanation.value) {
      if (explanationMode.value === "edit") {
        issues.push("Finish editing the explanation before approval.");
      } else if (explanationMode.value === "new") {
        issues.push("Complete the new explanation before approval.");
      } else if (explanationMode.value === "duplicate") {
        issues.push("Finish duplicating the explanation before approval.");
      } else {
        issues.push("Resolve pending explanation changes before approval.");
      }
    }

    if (!explanationLinked.value) {
      issues.push("Link an explanation to the question.");
    }

    if (!similarityApprovalReadyState.value) {
      issues.push(SIMILARITY_APPROVAL_REQUIRED_MESSAGE);
    }

    return issues;
  });

  return {
    isQuestionComplete,
    allOptionsHaveContent,
    // Backward-compatible alias used by existing views.
    allOptionsHaveText: allOptionsHaveContent,
    getMissingAnswers: missingOptionLabels,
    unmetApprovalCriteria,
    questionTextPresent,
    categorySelected,
    explanationLinked,
    isCurrentlyEditingExplanation,
    requiresSimilarityApproval: requiresSimilarityApprovalCheck,
    similarityApprovalReady: similarityApprovalReadyState,
  };
};
